import type { SerializeFrom } from '~/types/serialize-from';
import { useEffect } from 'react';
import { useFetcher } from 'react-router';
import { atom, useAtom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import {
    FEATURED_RESULTS_ACTION,
    type loader,
} from '~/routes/actions.search.featured-results';
import { useIsGoodBot } from '@modules/root';

const trendingSearchResultsAtom = atomWithStorage<
    NonNullable<SerializeFrom<typeof loader>>['results'] | null
>('trending-search-results', null);

/**
 * Track whether the trending results have already been requested during the
 * current page lifecycle.
 */
const isFetchedAtom = atom(false);
const isLoadedAtom = atom(false);

/**
 * Hook to fetch trending search results for the mobile search modal.
 * Returns a curated list of trending results with title, link, and type
 * information, while persisting the latest payload in browser storage.
 */
export function useTrendingSearchResults() {
    const fetcher = useFetcher<typeof loader>({
        key: 'hook:useTrendingSearchResults',
    });
    const isBot = useIsGoodBot();
    const [trendingSearchResults, setTrendingSearchResults] = useAtom(
        trendingSearchResultsAtom,
    );
    const [isFetched, setIsFetched] = useAtom(isFetchedAtom);
    const [isLoaded, setIsLoaded] = useAtom(isLoadedAtom);

    useEffect(() => {
        if (isBot || isLoaded) {
            return;
        }
        if (!fetcher.data && fetcher.state === 'idle' && !isFetched) {
            fetcher.load(FEATURED_RESULTS_ACTION);
            setIsFetched(true);
        } else if (fetcher.data) {
            setTrendingSearchResults(fetcher.data.results);
            setIsFetched(true);
            setIsLoaded(true);
        }
    }, [
        fetcher,
        isBot,
        isFetched,
        isLoaded,
        setIsFetched,
        setIsLoaded,
        setTrendingSearchResults,
    ]);

    return {
        trendingSearchResults,
    };
}
